import {
  queueMoved,
  SHORT_GAP_MS,
  SHORT_PLATFORMS,
  SHORT_TRIES,
  type ShortPlatform,
  type ShortReview,
  type ShortsView,
  type ShortView,
  shortQueue,
  shortsGoing,
} from '@gardener/protocol';
import { createMemo, createResource, createSignal, For, Match, onCleanup, Show, Switch } from 'solid-js';
import { call } from './api';
import {
  ago,
  count,
  EmptyState,
  Icon,
  LoadingLine,
  optimistic,
  TOOLBAR_CONTROL,
  Toolbar,
  ToolbarGroup,
  toast,
  Well,
  when,
} from './ui';

/** The list comes again this often (ms): a post Buffer is still sending shows as sent within it. */
const POLL_MS = 15_000;
/** How often the server looks for a free slot (its `DUE_EVERY_MS`). */
const LOOK_MS = 5 * 60_000;
/** How tall the player's controls are, at most (px): a press there scrubs, never lifts the card. */
const CONTROLS_PX = 48;

const NAMES: Record<ShortPlatform, string> = { youtube: 'YouTube', tiktok: 'TikTok', instagram: 'Instagram' };

/** Where a short stands, all platforms taken together. */
type State = 'waiting' | 'queued' | 'partly' | 'posted' | 'denied';
function stateOf(s: ShortView): State {
  const on = SHORT_PLATFORMS.filter((p) => s.posts[p]).length;
  if (on === SHORT_PLATFORMS.length) return 'posted';
  if (on) return 'partly';
  return s.review === 'approved' ? 'queued' : s.review;
}
/** The tabs: which states each shows. */
const TABS = {
  review: { title: 'To review', states: ['waiting'] },
  queued: { title: 'Queued', states: ['queued', 'partly'] },
  posted: { title: 'Posted', states: ['posted'] },
  denied: { title: 'Denied', states: ['denied'] },
  all: { title: 'All', states: ['waiting', 'queued', 'partly', 'posted', 'denied'] },
} as const satisfies Record<string, { title: string; states: readonly State[] }>;
type Tab = keyof typeof TABS;

const clock = (t: number) => new Date(t).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

/**
 * When each approved short should go out (ms): the queue in its order, a slot (`SHORT_GAP_MS`) apart from the first
 * platform free, none for one that can't go out; the server looks every few minutes.
 */
function etas(shorts: readonly ShortView[], now: number): Map<string, number> {
  const free = Math.min(
    ...SHORT_PLATFORMS.map((p) =>
      Math.max(now, ...shorts.map((s) => Date.parse(s.posts[p]?.at ?? '') + SHORT_GAP_MS || 0)),
    ),
  );
  return new Map(shortsGoing(shorts).map((s, i) => [s.id, free + i * SHORT_GAP_MS]));
}

/** A short waiting in the queue, as the Queued tab shows it. */
interface Turn {
  /** Its place in the queue, from 0. */
  place: number;
  last: boolean;
  /** The next free slot posts it: the first that can go out, which may not be the first in the queue. */
  next: boolean;
  moveTo: (place: number) => void;
  /** A drag has it, or holds another over it (a drop takes its place). */
  lifted: boolean;
  over: boolean;
}

/** The queued card a drag event is at: its short's id. */
const queuedAt = (e: DragEvent) =>
  e.target instanceof Element ? e.target.closest<HTMLElement>('[data-queued]')?.dataset.queued : undefined;

/**
 * The shorts (ADR-0161): every short tools/shorts made, newest first, to approve or deny. An approved one waits its turn
 * (`SHORT_GAP_MS` a platform) and is posted by the server; until then Undo takes it back, and on the Queued tab, in the
 * queue's order, a drag or its arrows move it. Each posted one with its links and how it did.
 */
export function Shorts() {
  const [view, { refetch, mutate }] = createResource(() => call<ShortsView>('GET', '/api/admin/shorts'));
  const timer = setInterval(() => void refetch(), POLL_MS);
  onCleanup(() => clearInterval(timer));
  const [tab, setTab] = createSignal<Tab>('review');
  const now = () => Date.now();
  /** The list, or nothing while it loads or after a failed call (reading a failed resource throws). */
  const data = () => (view.error ? undefined : view());
  const shorts = () => data()?.shorts ?? [];
  const counts = createMemo(() => {
    const n = Object.fromEntries(Object.keys(TABS).map((t) => [t, 0])) as Record<Tab, number>;
    for (const s of shorts())
      for (const [t, { states }] of Object.entries(TABS))
        if ((states as readonly State[]).includes(stateOf(s))) n[t as Tab]++;
    return n;
  });
  const byId = createMemo(() => new Map(shorts().map((s) => [s.id, s])));
  const queue = createMemo(() => shortQueue(shorts()));
  const next = createMemo(() => shortsGoing(shorts())[0]?.id);
  /** The Queued tab reads as the queue: those part way out, then who goes next. */
  const shown = () =>
    tab() === 'queued'
      ? [...shorts().filter((s) => stateOf(s) === 'partly'), ...queue()]
      : shorts().filter((s) => (TABS[tab()].states as readonly State[]).includes(stateOf(s)));
  const eta = createMemo(() => etas(shorts(), now()));
  /** Puts `after` in place of its short in the list. */
  const put = (after: ShortView) => {
    const v = data();
    if (v) mutate({ ...v, shorts: v.shorts.map((x) => (x.id === after.id ? after : x)) });
  };

  const review = (s: ShortView, review: ShortReview) =>
    optimistic(
      () => {
        put({ ...s, review });
        return () => put(s);
      },
      () => call<ShortView>('PATCH', `/api/admin/shorts/${encodeURIComponent(s.id)}`, { review }),
      put,
    );
  /** One move at a time: the server's is several writes, and the next reads the queue they leave. */
  let moving: Promise<unknown> = Promise.resolve();
  /** Short `id` takes place `to` in the queue, those between closing up. */
  const moveTo = (id: string, to: number) => {
    const v = data();
    const ids = queue().map((s) => s.id);
    const before = (to > ids.indexOf(id) ? ids[to + 1] : ids[to]) ?? null;
    const moved = queueMoved(queue(), id, before);
    if (!v || !moved?.size) return;
    const sent = moving.then(() => call('POST', `/api/admin/shorts/${encodeURIComponent(id)}/move`, { before }));
    const settled = sent.catch(() => {});
    moving = settled;
    void optimistic(
      () => {
        mutate({
          ...v,
          shorts: v.shorts.map((s) => (moved.has(s.id) ? { ...s, reviewed: moved.get(s.id) as string } : s)),
        });
        return () => void refetch();
      },
      () => sent,
      () => {
        // The list again after the last move: a poll answered before it landed may have shown the old order.
        if (moving === settled) void refetch();
      },
    );
  };
  const [lifted, setLifted] = createSignal<string>();
  const [over, setOver] = createSignal<string>();
  const turnOf = (id: string): Turn | undefined => {
    const place = tab() === 'queued' ? queue().findIndex((s) => s.id === id) : -1;
    if (place < 0) return undefined;
    return {
      place,
      last: place === queue().length - 1,
      next: next() === id,
      moveTo: (to) => moveTo(id, to),
      lifted: lifted() === id,
      over: over() === id && lifted() !== id,
    };
  };
  const measure = async () => {
    try {
      mutate(await call<ShortsView>('POST', '/api/admin/shorts/measure'));
      toast('Stats read');
    } catch (err) {
      toast((err as Error).message, 'error');
    }
  };

  return (
    <div class="@container flex w-full flex-col gap-2 p-2">
      <Toolbar>
        <div class="max-w-full min-w-0 overflow-x-auto [scrollbar-width:none]">
          <ToolbarGroup
            label="Shorts"
            value={tab()}
            options={(Object.keys(TABS) as Tab[]).map((t) => [t, TABS[t].title, counts()[t]] as const)}
            onChange={setTab}
          />
        </div>
        <Show when={data()?.posting}>
          <button
            type="button"
            class={TOOLBAR_CONTROL}
            title="Stats are read every 6 hours"
            onClick={() => void measure()}
          >
            <Icon name="refresh" class="size-3.5" />
            Read stats now
          </button>
        </Show>
      </Toolbar>
      <Show when={data() && !data()?.posting}>
        <Well label="Posting">
          <div class="text-fg-muted flex items-center gap-2 px-3 py-2.5 text-[13px]" role="status">
            <Icon name="danger" class="size-4 shrink-0" />
            Posting is off: the server has no SHORTS_BUFFER_KEY.
          </div>
        </Well>
      </Show>
      <Switch>
        <Match when={view.error}>
          <Well label="Shorts">
            <div class="text-fg-muted flex items-center gap-2 px-3 py-2.5 text-[13px]" role="alert">
              <Icon name="danger" class="size-4 shrink-0" />
              {(view.error as Error).message}
            </div>
          </Well>
        </Match>
        <Match when={!data()}>
          <Well label="Shorts">
            <div class="px-3 py-2.5">
              <LoadingLine label="Loading shorts" />
            </div>
          </Well>
        </Match>
        <Match when={!shown().length}>
          <Well label="Shorts">
            <EmptyState centered title={tab() === 'review' ? 'Nothing to review' : 'No shorts here'} />
          </Well>
        </Match>
        <Match when={shown().length}>
          <ul
            class={GRID}
            aria-label="Shorts"
            onDragStart={(e) => {
              // Selected text drags too; only the picture lifts its card, and not from the player's controls.
              const picture = e.target instanceof HTMLElement && e.target.draggable ? e.target : null;
              const id = picture && queuedAt(e);
              if (!id || !e.dataTransfer) return;
              const video = picture.querySelector('video')?.getBoundingClientRect();
              if (video && e.clientY > video.bottom - CONTROLS_PX) return e.preventDefault();
              setLifted(id);
              e.dataTransfer.effectAllowed = 'move';
              // Firefox starts no drag without data.
              e.dataTransfer.setData('text/plain', id);
            }}
            onDragOver={(e) => {
              const id = lifted() && queuedAt(e);
              setOver(id || undefined);
              if (id) e.preventDefault();
            }}
            onDrop={(e) => {
              e.preventDefault();
              const [id, onto] = [lifted(), queuedAt(e)];
              if (id && onto)
                moveTo(
                  id,
                  queue().findIndex((s) => s.id === onto),
                );
            }}
            onDragEnd={() => {
              setLifted();
              setOver();
            }}
          >
            {/* By id: a poll's fresh copies keep each card (its open Details, a video playing). */}
            <For each={shown().map((s) => s.id)}>
              {(id) => (
                <Show when={byId().get(id)}>
                  {(s) => (
                    <Card
                      s={s()}
                      now={now()}
                      eta={eta().get(id)}
                      review={(r) => void review(s(), r)}
                      turn={turnOf(id)}
                    />
                  )}
                </Show>
              )}
            </For>
          </ul>
        </Match>
      </Switch>
    </div>
  );
}

/** The cards' grid: as many 9:16 columns as fit (logdash's `DomainsHome` grid, its columns sized for a short). */
const GRID = 'grid grid-cols-[repeat(auto-fill,minmax(14rem,1fr))] items-start gap-2';

/** A short as logdash's `DomainCard`: its title and age in the header, the video, its posts, the call to make. */
function Card(props: {
  s: ShortView;
  now: number;
  eta: number | undefined;
  review: (r: ShortReview) => void;
  turn: Turn | undefined;
}) {
  const s = () => props.s;
  const state = () => stateOf(s());
  const due = () => {
    const t = props.eta;
    if (t === undefined) return '';
    return t - props.now <= LOOK_MS ? 'in a few minutes' : `about ${clock(t)}`;
  };
  const tried = () => SHORT_PLATFORMS.some((p) => s().posts[p] || s().failed[p]);
  return (
    <li
      class="bg-surface-25-bg flex min-w-0 flex-col gap-2 rounded-2xl p-2"
      classList={{
        'opacity-40': props.turn?.lifted,
        'outline-brand outline-2 outline-offset-2': props.turn?.over,
      }}
      aria-label={s().title}
      data-queued={props.turn ? s().id : undefined}
    >
      <header class="flex h-7 min-w-0 items-center gap-2.5 px-3">
        <h3 class="min-w-0 truncate text-[13px] font-medium" title={s().title}>
          {s().title}
        </h3>
        <span class="text-fg-muted ml-auto shrink-0 text-xs tabular-nums" title={when(s().made)}>
          {ago(s().made, props.now)}
        </span>
      </header>
      {/* Only the picture drags: the title and Details stay selectable. */}
      <div class="relative" classList={{ 'cursor-grab': !!props.turn }} draggable={!!props.turn}>
        <Show
          when={s().media}
          fallback={
            <div class="bg-surface-150-bg text-fg-muted flex aspect-[9/16] items-center justify-center rounded-lg text-sm">
              No video uploaded
            </div>
          }
        >
          {(media) => (
            <div class="edge-over aspect-[9/16] overflow-hidden rounded-lg bg-black">
              {/* The player's controls only under the pointer: at rest, the short's own frame. */}
              <video
                class="block size-full object-cover"
                src={`${media()}#t=${s().cover}`}
                poster={s().thumb ?? undefined}
                preload="metadata"
                playsinline
                onPointerEnter={(e) => {
                  e.currentTarget.controls = true;
                }}
                onPointerLeave={(e) => {
                  e.currentTarget.controls = false;
                }}
              >
                <track kind="captions" />
              </video>
            </div>
          )}
        </Show>
        <Show when={props.turn}>{(turn) => <Arrows turn={turn()} title={s().title} />}</Show>
      </div>
      <Show when={state() === 'waiting' && s().check?.length}>
        <p class="text-error-fg px-3 text-xs" role="status">
          Failed its check: {s().check?.join('; ')}
        </p>
      </Show>
      <Show when={tried()}>
        <ul class="flex flex-col gap-0.5">
          <For each={SHORT_PLATFORMS}>{(p) => <Post name={NAMES[p]} s={s()} p={p} />}</For>
        </ul>
      </Show>
      <Show when={state() !== 'posted'}>
        <div class="flex min-h-8 items-center gap-2">
          <Show when={state() === 'waiting'}>
            <button
              type="button"
              class="ld-button flex-1"
              data-size="sm"
              data-variant="primary"
              onClick={() => props.review('approved')}
            >
              <Icon name="check" class="size-4" />
              Approve
            </button>
            <button type="button" class="ld-button flex-1" data-size="sm" onClick={() => props.review('denied')}>
              Deny
            </button>
          </Show>
          <Show when={state() === 'queued'}>
            <span class="ld-badge ml-1 min-w-0 truncate" data-size="sm" data-variant="warning">
              Queued{due() && ` · ${due()}`}
            </span>
            <Undo review={props.review} />
          </Show>
          <Show when={state() === 'denied'}>
            <span class="ld-badge ml-1" data-size="sm" data-variant="error">
              Denied
            </span>
            <Undo review={props.review} />
          </Show>
          <Show when={state() === 'partly'}>
            <span class="ld-badge ml-1" data-size="sm" data-variant="success">
              Posting
            </span>
          </Show>
        </div>
      </Show>
      <details class="ld-collapse">
        <summary class="ld-collapse-title text-fg-muted hover:text-fg-default transition-ink flex h-8 items-center gap-1.5 px-3 py-0 text-[13px] font-medium">
          Details
          <Icon name="chevron" class="in-open:rotate-180 size-3.5 transition-transform" />
        </summary>
        <dl class="ld-collapse-content grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1.5 px-3 pt-1 pb-2 text-xs">
          <dt class="text-fg-muted">Moments</dt>
          <dd class="text-fg-secondary">
            {s()
              .moments.map((m) => m.label)
              .join(' → ')}
          </dd>
          <dt class="text-fg-muted">Recipe</dt>
          <dd class="text-fg-secondary">
            {Object.entries(s().recipe)
              .map(([knob, option]) => `${knob} ${option}`)
              .join(' · ')}
          </dd>
          <Show when={state() !== 'waiting' && s().check?.length}>
            <dt class="text-fg-muted">Failed check</dt>
            <dd class="text-error-fg">{s().check?.join('; ')}</dd>
          </Show>
          <Show when={s().reward !== null}>
            <dt class="text-fg-muted">Reward</dt>
            <dd class="text-fg-secondary">
              <span class="tabular-nums">{s().reward?.toFixed(2)}</span>: its views against each platform’s usual
            </dd>
          </Show>
          <dt class="text-fg-muted">Caption</dt>
          <dd class="text-fg-secondary whitespace-pre-wrap">{s().captions.tiktok}</dd>
          <dt class="text-fg-muted">Length</dt>
          <dd class="text-fg-secondary tabular-nums">{s().seconds.toFixed(1)} s</dd>
          <dt class="text-fg-muted">Made</dt>
          <dd class="text-fg-secondary">{when(s().made)}</dd>
          <dt class="text-fg-muted">Id</dt>
          <dd class="text-fg-secondary font-mono break-all">{s().id}</dd>
        </dl>
      </details>
    </li>
  );
}

/**
 * A queued short's place (the next to go out says so) and the keys that move it a place, over its picture: what a drag
 * does, by keyboard too.
 */
function Arrows(props: { turn: Turn; title: string }) {
  const key = (way: -1 | 1, words: string, off: () => boolean, icon: string) => (
    <button
      type="button"
      class="ld-button"
      data-size="xs"
      data-shape="square"
      disabled={off()}
      aria-label={`Move ${props.title} ${words}`}
      title={`Go out ${words}`}
      onClick={(e) => {
        props.turn.moveTo(props.turn.place + way);
        // The list just moved under it, which takes the focus away.
        e.currentTarget.focus();
      }}
    >
      <Icon name="arrow" class={`size-3.5 ${icon}`} />
    </button>
  );
  return (
    <div class="absolute inset-x-1.5 top-1.5 flex items-center justify-between">
      {key(-1, 'earlier', () => props.turn.place === 0, 'rotate-180')}
      <span class="ld-badge tabular-nums" data-size="sm" data-variant={props.turn.next ? 'inverse' : undefined}>
        {props.turn.next ? 'Posts next' : `#${props.turn.place + 1}`}
      </span>
      {key(1, 'later', () => props.turn.last, '')}
    </div>
  );
}

/** Takes a review back: the short waits again. */
function Undo(props: { review: (r: ShortReview) => void }) {
  return (
    <button
      type="button"
      class="ld-button ml-auto"
      data-size="sm"
      data-variant="ghost"
      onClick={() => props.review('waiting')}
    >
      Undo
    </button>
  );
}

/**
 * One platform's line as logdash's `BreakdownList` row with a `StatusBadge` dot: its post (a link, views, share
 * watched), Buffer still sending it, its failures, or nothing yet.
 */
function Post(props: { name: string; s: ShortView; p: ShortPlatform }) {
  const post = () => props.s.posts[props.p];
  const failed = () => props.s.failed[props.p];
  const stats = () => post()?.stats;
  const dot = () => {
    if (post()) return post()?.sending ? 'bg-warning' : 'bg-success';
    return failed() ? 'bg-error' : 'bg-idle';
  };
  return (
    <li class="flex h-8 items-center gap-3 rounded-lg px-3 text-sm">
      <span class="flex min-w-0 flex-1 items-center gap-2">
        <span class={`ld-status-dot shrink-0 ${dot()}`} aria-hidden="true" />
        <Show when={post()?.url} fallback={<span class="truncate">{props.name}</span>}>
          {(url) => (
            <a
              href={url()}
              target="_blank"
              rel="noreferrer"
              class="hover:text-fg-secondary focus-visible:outline-brand transition-ink inline-flex min-w-0 items-center gap-1 rounded-sm focus-visible:outline-2 focus-visible:outline-offset-2"
            >
              <span class="truncate">{props.name}</span>
              <Icon name="external" class="text-fg-muted size-3 shrink-0" />
            </a>
          )}
        </Show>
      </span>
      <Show
        when={post()}
        fallback={
          <Show when={failed()} fallback={<span class="text-fg-muted shrink-0">not yet</span>}>
            {(f) => (
              <span class="text-error-fg shrink-0 tabular-nums" title={f().error}>
                {f().maybe ? 'may be out' : f().tries >= SHORT_TRIES ? 'given up' : `failed ${f().tries}×`}
              </span>
            )}
          </Show>
        }
      >
        <Show when={!post()?.sending} fallback={<span class="text-fg-muted shrink-0">sending…</span>}>
          <span class="text-fg-secondary shrink-0 tabular-nums">
            {stats()
              ? `${count(stats()?.views ?? 0)} views${stats()?.watched === undefined ? '' : ` · ${Math.round((stats()?.watched ?? 0) * 100)}%`}`
              : 'posted'}
          </span>
        </Show>
      </Show>
    </li>
  );
}
